import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnDestroy,
    inject,
    input,
    output,
    viewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import {
    AixDataTestingDirective,
    AixLoadingComponent,
    AixNotificationComponent,
    AixPdfViewerComponent
} from '@trade-platform/ui-components';
import { DocumentViewerDownloadService, PDF_WORKER_SRC } from '@trade-platform/ui-shared';
import { LogService } from '@trade-platform/ui-utils';
import { Logger } from 'typescript-logging';
// @ts-ignore: legacy build has no type declarations
import * as PDFJS from 'pdfjs-dist/legacy/build/pdf.mjs';
import { ORDERS_STORE_FACADE } from '../../base.orders.store.facade';

/**
 * Full-screen overlay that renders one order form as a filled PDF.
 *
 * Deliberately an overlay rather than a routed page: the process page stays mounted, so
 * the user keeps their place in the form and the dirty-form deactivate guard never fires.
 *
 * Also deliberately NOT built on <aix-ui-document-viewer>: that component drives the
 * shared global `documentViewer` NgRx slice, which is live for the order document viewer
 * and would collide with a still-mounted form page. It also never checks response.ok on
 * its raw fetch, so a 4xx from this brand-new endpoint would surface as a misleading
 * "this document type can't be opened here".
 */
@Component({
    selector: 'aix-order-form-preview',
    templateUrl: './form-preview.html',
    styleUrls: ['./form-preview.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AixPdfViewerComponent,
        AixLoadingComponent,
        AixNotificationComponent,
        AixDataTestingDirective
    ]
})
export class AixOrderFormPreviewComponent implements OnDestroy {
    private storeFacade = inject(ORDERS_STORE_FACADE);
    private docDownloader = inject(DocumentViewerDownloadService);
    private LOG: Logger = inject(LogService).getLogger('app.modules.orders-process.form-preview');
    private cd = inject(ChangeDetectorRef);

    orderId = input.required<string>();
    formId = input.required<string>();
    formName = input<string>('');

    closed = output<void>();

    readonly pdfViewer = viewChild(AixPdfViewerComponent);

    isVisible = false;
    isLoading = false;
    loadFailed = false;
    pdfJsReady = false;
    pdfDataSource: { data: Uint8Array } | null = null;

    /**
     * The bytes of the document currently on screen. Held for the Download button, and
     * dropped as soon as the overlay closes or a new fetch starts — deliberately NOT a cache
     * across opens, see open().
     *
     * A Blob rather than the Uint8Array: pdf.js transfers the typed array's backing
     * ArrayBuffer to its worker and detaches it, so the view handed to the viewer is
     * unreadable afterwards. Download derives a fresh view from the Blob instead.
     */
    private currentBlob: Blob | null = null;
    /**
     * The in-progress answers to render. Held rather than passed straight through, because
     * every refetch -- a retry, or a reopen -- has to send the same draft the overlay was
     * opened with.
     */
    private draft: unknown = null;
    /**
     * Identifies the newest request. `documentKey()` cannot: every open refetches, so two
     * requests for the SAME order and form overlap routinely — one opened before a save and
     * the fresh one opened after it. Both carry the same key, so without a token the older
     * completion would pass the guard and overwrite the newer render with pre-save bytes.
     */
    private requestSeq = 0;
    private previewSub: Subscription | null = null;
    private subscriptions: Subscription[] = [];

    constructor() {
        if (!PDFJS.GlobalWorkerOptions.workerSrc) {
            Promise.all([
                // @ts-ignore: only accepted in commonJs or esNext
                import(/* webpackChunkName: "PDFJS" */ 'pdfjs-dist/legacy/build/pdf.min.mjs')
            ]).then(() => {
                PDFJS.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
                this.pdfJsReady = true;
                this.cd.markForCheck();
            });
        } else {
            this.pdfJsReady = true;
        }
    }

    /**
     * Always refetches, so what is on screen is always the order's current saved state.
     *
     * The render depends on far more than the order and form ids: the form's saved answers,
     * its stamping map, comment resolutions, and rule evaluation that adds or removes fields.
     * Any cache therefore has to be invalidated from every path that can touch those. An
     * earlier version keyed on `orderId::formId` and refreshed only when the save had been
     * started from the Preview button, which served a stale render after a plain Save
     * Progress (trade-platform#10389). Previewing is a deliberate click made a handful of
     * times per form, so the extra request costs less than the invalidation it replaces.
     */
    open(draft: unknown = null) {
        this.draft = draft;
        this.isVisible = true;
        this.loadFailed = false;
        this.lockBackgroundScroll();
        this.fetch();
    }

    close() {
        this.isVisible = false;
        this.pdfDataSource = null;
        // Nothing survives a close: the next open renders whatever is saved then, and a
        // Download cannot reach a document that is no longer on screen. That has to include a
        // request still in flight -- without bumping the token it would pass both guards and
        // repopulate the bytes behind a closed overlay.
        this.previewSub?.unsubscribe();
        this.requestSeq++;
        this.currentBlob = null;
        this.draft = null;
        this.releaseBackgroundScroll();
        this.closed.emit();
        this.cd.markForCheck();
    }

    retry() {
        this.open(this.draft);
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.isVisible) {
            this.close();
        }
    }

    onClickDownload() {
        // Gate on pdfDataSource, not just the blob: it is the only evidence pdf.js actually
        // rendered these bytes. Without it a 200 carrying a truncated PDF, or an upstream
        // HTML/JSON error body, is exportable from behind the error state.
        if (!this.currentBlob || !this.pdfDataSource) {
            return;
        }
        this.docDownloader.downloadDocument(this.currentBlob, this.formName() || 'form-preview');
    }

    onLoadDocumentError(error: Error) {
        this.loadFailed = true;
        this.pdfDataSource = null;
        // These bytes are unrenderable, so drop them rather than leave them downloadable.
        this.currentBlob = null;
        this.LOG.error(() => 'Failed to render an order form preview', error);
        this.cd.markForCheck();
    }

    zoomIn() {
        this.pdfViewer()?.zoomIn();
    }

    zoomOut() {
        this.pdfViewer()?.zoomOut();
    }

    rotate() {
        this.pdfViewer()?.rotate();
    }

    private fetch() {
        // Pin the WHOLE key this request is for, not just the form. `orderId` and `formId` are
        // both live inputs, and the process route keeps this component mounted across
        // param-only navigation, so either can change mid-flight. Comparing form alone let an
        // order-A response pass the guard and then get written under order B's key.
        const requestedOrderId = this.orderId();
        const requestedFormId = this.formId();
        const requestedKey = this.documentKey();
        const requestedSeq = ++this.requestSeq;

        // Nothing wants the superseded request's bytes, so stop listening for them rather
        // than relying on the guards alone.
        this.previewSub?.unsubscribe();

        this.isLoading = true;
        // Drop the previous document up front: from here on it is not what was asked for, so
        // it must not be renderable or downloadable even if this request fails.
        this.pdfDataSource = null;
        this.currentBlob = null;
        this.cd.markForCheck();

        this.previewSub = this.storeFacade
            .getFormPreview(requestedOrderId, requestedFormId, this.draft)
            .pipe(first())
            .subscribe({
                next: blob => {
                    // render() re-checks after its await and owns every write, so a
                    // superseded response cannot reach the view.
                    void this.render(blob, requestedKey, requestedSeq);
                },
                error: error => {
                    if (requestedSeq !== this.requestSeq || this.documentKey() !== requestedKey) {
                        return;
                    }
                    this.isLoading = false;
                    this.loadFailed = true;
                    this.LOG.error(() => 'Failed to fetch an order form preview', error);
                    this.cd.markForCheck();
                }
            });
    }

    /**
     * Identifies the document a request is for. Order AND form: `formId` is a Documents
     * primary key shared by every order using the same template, so it alone cannot
     * distinguish two orders' previews.
     */
    private documentKey() {
        return `${this.orderId()}::${this.formId()}`;
    }

    /**
     * The single place the current bytes and the rendered view are written, and the only
     * place that decides whether a completion is still current. Both checks are re-run after
     * the await, because decoding is async: `requestSeq` rejects an older request for the same
     * document, and the key rejects a response whose `orderId`/`formId` has since changed.
     */
    private async render(blob: Blob, key: string, seq: number) {
        const buffer = await blob.arrayBuffer();

        if (seq !== this.requestSeq || this.documentKey() !== key) {
            return;
        }

        this.currentBlob = blob;
        this.pdfDataSource = { data: new Uint8Array(buffer) };
        this.isLoading = false;
        this.loadFailed = false;
        this.cd.markForCheck();
    }

    ngOnDestroy() {
        // Load-bearing: without this, navigating away while the overlay is open leaves the
        // body permanently unscrollable, because close() never runs.
        this.releaseBackgroundScroll();
        this.previewSub?.unsubscribe();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private lockBackgroundScroll() {
        document.body.style.overflow = 'hidden';
    }

    private releaseBackgroundScroll() {
        document.body.style.removeProperty('overflow');
    }
}
